import React, {useMemo, useState} from 'react';
import {
  Col,
  Image,
  Row,
  Text,
  TouchableOpacity,
  View,
} from '@unthinkable/react-core-components';
import {getRangeData, getZeroTimeDate, rangeCalculator} from './DateUtility';
import {DateRange} from './DateRange';
import {AutoComplete} from '@unthinkable/react-autocomplete';
import DateInput from '@unthinkable/react-date-picker/src/DatePicker';

const dateSelectorData = [
  {label: 'All', value: 'All'},
  {label: 'Day', value: 'Day'},
  {label: 'Week', value: 'Week'},
  {label: 'Month', value: 'Month'},
  {label: 'Year', value: 'Year'},
  {label: 'Financial Year', value: 'FY'},
  {label: 'Custom', value: 'Custom'},
];

export const DateFilter = ({
  onChangeValue,
  value,
  options = dateSelectorData,
  styles = {},
  direction = 'row',
  rangeCalendar = false,
}) => {
  const [error, setError] = useState('');
  const {
    gap,
    previousIcon,
    nextIcon,
    containerStyle,
    textStyle,
    iconContainerStyle,
    iconStyle,
    autoCompleteTheme,
  } = styles;

  const {filterOf = 'All', from, to} = value || {};

  let displayRange = useMemo(() => rangeCalculator(value), [value]);

  const applyFilter = ({from, to, filterOf, source} = {}) => {
    value = {...value};
    if (source === 'Reset' || source === 'All') {
      value = {};
    } else {
      if (source !== 'Prev' && source !== 'Next') {
        const dateData = getRangeData({filterOf, from, to});
        if (
          filterOf == 'Day' ||
          filterOf == 'Week' ||
          filterOf == 'Month' ||
          filterOf == 'Year' ||
          filterOf == 'FY' ||
          (filterOf == 'Custom' && source !== 'Toggle')
        ) {
          from = dateData?.from;
          to = dateData?.to;
        } else if (filterOf == 'Custom' && source === 'Toggle') {
          if (value) {
            from = value.from;
            to = value.to;
          }
        }
      }
      value = {
        from: from,
        to: to,
        filterOf,
      };
    }

    onChangeValue && onChangeValue(value);
  };

  const selectorOnChange = val => {
    applyFilter({filterOf: val?.value, source: 'Toggle'});
  };

  const onDateChange = newValue => {
    onChangeValue({...value, ...newValue});
  };

  const PrevNextFunctionOfDate = ({source}) => {
    const dateData = getRangeData({source, from, to, filterOf});
    let _from = dateData?.from;
    let _to = dateData?.to;
    applyFilter({from: _from, to: _to, source, filterOf});
  };

  const getNavigationComponent = ({source, icon}) => {
    const style = {};
    if (source === 'Prev') {
      style.paddingLeft = 8;
    } else {
      style.paddingRight = 8;
    }
    return filterOf && filterOf !== 'All' && filterOf !== 'Custom' ? (
      <TouchableOpacity
        onPress={() => PrevNextFunctionOfDate({source})}
        style={{cursor: 'pointer', ...style, ...iconContainerStyle}}
        title={`${source} ${filterOf}`}>
        <Image source={icon} style={iconStyle} />
      </TouchableOpacity>
    ) : (
      void 0
    );
  };

  let component = void 0;
  let containerExtraStyle = direction === 'row' ? {} : {width: '100%'};
  if (displayRange) {
    component = (
      <View
        style={{
          ...containerStyle,
          ...containerExtraStyle,
        }}>
        {getNavigationComponent({
          source: 'Prev',
          icon: previousIcon,
        })}
        <Text style={textStyle}>{displayRange}</Text>
        {getNavigationComponent({
          source: 'Next',
          icon: nextIcon,
        })}
      </View>
    );
  }
  if (filterOf === 'Custom') {
    if (rangeCalendar) {
      component = (
        <DateRange
          styles={styles}
          value={{from, to}}
          onDateChange={onDateChange}>
          <View
            style={{
              ...containerStyle,
              ...containerExtraStyle,
            }}>
            <Text style={textStyle}>{displayRange}</Text>
          </View>
        </DateRange>
      );
    } else {
      const onFromDateChange = from => {
        const startDateFrom = getZeroTimeDate(from);
        if (value.to && startDateFrom && new Date(value.to) < new Date(startDateFrom)) {
          setError('To date can not be less than from date');
          onDateChange({from: void 0});
        } else {
          onDateChange({from: startDateFrom});
          setError('');
        }
      };
      const onToDateChange = to => {
        const endateTo = getZeroTimeDate(to, true);
        if (value.from && endateTo && new Date(endateTo) < new Date(value.from)) {
          setError('To date can not be less than from date');
          onDateChange({to: void 0});
        } else {
          onDateChange({to: endateTo});
          setError('');
        }
      };
      component = (
        <Col gap={4}>
          <DateInput
            styles={styles?.datePicker}
            label="From"
            value={from}
            onChangeValue={onFromDateChange}
          />
          <DateInput
            styles={styles?.datePicker}
            label="To"
            value={to}
            onChangeValue={onToDateChange}
            error={error}
          />
        </Col>
      );
    }
  }

  const Comp = direction === 'row' ? Row : Col;
  let compStyle = direction === 'row' ? {alignItems: 'center'} : {};
  let filterDisplayValue = filterOf
    ? options?.find(option => option.value === filterOf)?.label
    : void 0;
  return (
    <Comp gap={gap} style={compStyle}>
      <View
        style={{
          ...(direction === 'row' ? {width: 100} : {width: '100%'}),
        }}>
        <AutoComplete
          styles={autoCompleteTheme}
          options={options}
          suggestionField="label"
          value={filterDisplayValue}
          onChangeValue={selectorOnChange}
          allowClear={false}
          showArrow
          fetch={() => options}
        />
      </View>
      {component}
    </Comp>
  );
};
